export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  // Novo formato: permitir enviar `user` + `account`, ou apenas `account` quando
  // o usuário já estiver autenticado (órfão). Manter compatibilidade com o
  // formato antigo lendo top-level fields quando presentes.
  user?: {
    name: string;
    email: string;
    password: string;
  };
  account: {
    name: string;
    domain?: string;
    // CPF ou CNPJ (um dos dois) e telefone são obrigatórios no cadastro.
    cpf?: string;
    cnpj?: string;
    contactPhone: string;
  };
  planId?: string;
}

export interface ResetPasswordRequest {
  email: string;
}

export interface ChangePasswordRequest {
  token: string;
  password: string;
}

export interface AuthUser {
  id: string;
  accountId?: string;
  name: string;
  email: string;
  roles?: UserRole[];
}

export interface AuthAccount {
  accountId?: string;
  name: string;
  domain: string;
}

export interface AuthResponse {
  success: boolean;
  message?: string;
  user?: AuthUser;
  account?: AuthAccount;
  isOrphan?: boolean;
}

export interface ValidationError {
  field: string;
  message: string;
}

// Tipos para usuários
export interface User {
  id: string;
  accountId: string;
  name: string;
  email: string;
  financeCommunication: boolean;
  lastAccess: string | null;
  isActive: boolean;
  roles: string[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserRequest {
  name: string;
  email: string;
  financeCommunication: boolean;
  roles: string[];
}

export interface UpdateUserRequest {
  name: string;
  financeCommunication: boolean;
  roles: string[];
}

export interface UpdateCurrentUserRequest {
  name?: string;
  currentPassword?: string;
  newPassword?: string;
}

export interface UsersResponse {
  data: User[];
  pagination: {
    page: number;
    total: number;
  };
}

// Tipos para a lista de contas associadas ao usuário (GET /users/me/accounts)
export interface MeAccount {
  userId: string;
  accountId: string;
  username: string;
  financeCommunication?: boolean;
  accountName?: string;
  domain?: string;
  cnpj?: string;
  lastAccess?: string | null;
  isActive?: boolean;
  roles?: string[];
  createdAt?: string;
  updatedAt?: string;
}

export interface AccountsResponse {
  data: MeAccount[];
  Pagination: {
    page: number;
    total: number;
  };
}

export type UserRole =
  | "account-admin"
  | "finance-manager"
  | "analyst"
  | "reader";

export const USER_ROLES: { [key in UserRole]: string } = {
  "account-admin": "Administrador",
  "finance-manager": "Financeiro",
  analyst: "Analista",
  reader: "Leitor",
};

// Tipos para sistema de pagamento
export interface PaymentPlan {
  id: string;
  name: string;
  price: string;
  pageViewLimit: number;
  users: number;
  interval: string;
  features?: string[];
  dataRetentionPeriod?: string;
  isFreeTrial?: boolean;
  freeTrialPeriod?: string;
}

export interface BillingProduct {
  id: string;
  name: string;
  price: string;
  dataRetentionPeriod?: string;
  unitQuantity?: number;
  TargetPlan?: string | null;
  targetPlan?: string | null;
}

export interface SubscriptionItem {
  id: string;
  item: string;
  amount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSubscriptionItemRequest {
  id: string;
  amount: number;
}

export interface UpdateSubscriptionItemRequest {
  amount: number;
}

export interface AccountData {
  id: string;
  name: string;
  domain: string;
  country: string | null;
  language: string;
  currencyType: string;
  timezone: string;
  dateFormat: string;
  showcaseId: string | null;
  cpf: string | null;
  cnpj: string | null;
  legalName: string | null;
  tradeName: string | null;
  contactName: string | null;
  contactEmail: string;
  contactPhone: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateAccountRequest {
  name: string;
  domain?: string;
  cpf?: string;
  cnpj?: string;
  legalName: string | null;
  tradeName: string | null;
  contactName: string | null;
  contactEmail: string;
  contactPhone: string | null;
  language: string;
  currencyType: string;
  timezone: string;
  dateFormat: string;
}

export interface CheckoutRequest {
  planId: string;
}

export interface CheckoutResponse {
  checkoutLink: string;
}

// Tipos para assinaturas
export type SubscriptionFeature = "showcase" | "site-analytics";

export interface Subscription {
  id: string;
  accountId: string;
  subscriptionCheckoutId: string | null;
  externalId: string;
  externalCustomerId?: string | null;
  externalScheduleId?: string | null;
  name: string;
  status:
    | "active"
    | "canceled"
    | "past_due"
    | "unpaid"
    | "incomplete"
    | "trialing";
  value: string;
  pageViewLimit: number;
  isFreeTrial: boolean;
  users?: number;
  maxUsers: number;
  features: SubscriptionFeature[];
  interval: "month" | "year";
  billingDay: number;
  nextBillingDate: string;
  startedAt: string;
  endsAt: string | null;
  updatedAt: string;
}

export interface Plan {
  id: string;
  name: string;
  price: string;
  description?: string;
  features: string[];
  interval: "month" | "year";
  pageViewLimit: number;
}

export interface SubscriptionUpdatePreviewCatalogItem {
  id: string;
  name: string;
  type: string;
  price: string;
  interval: "month" | "year" | string;
  pageViewLimit?: number;
  users?: number;
  features?: string[];
}

export interface SubscriptionUpdatePreviewItem {
  catalogItem: SubscriptionUpdatePreviewCatalogItem;
  quantity: number;
}

export interface SubscriptionUpdatePreview {
  subtotal: string;
  discount: string;
  total: string;
  items: SubscriptionUpdatePreviewItem[];
  startsAt: string;
}

export interface SubscriptionNextInvoice {
  subtotal: string;
  discount: string;
  tax: string;
  balanceAdjustment: string;
  total: string;
  items: SubscriptionUpdatePreviewItem[];
  coupons: unknown[];
  billingDate: string;
}

// Tipos para métricas de hits do ciclo de faturamento
export interface HitsBillingCycleMetrics {
  billingCycleHits: number;
  billingCycleLimit: number;
  billingCyclePercentage: number;
  lastBillingDate: string;
  nextBillingDate: string;
}
